import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";
import { navigationGuard } from "@/services/authentification";
import { LoginCallback } from "@okta/okta-vue";
import { translate } from "@/i18n";

export const routes: Array<RouteRecordRaw> = [
  { path: "/index.html", redirect: "/" },
  { path: "/login/home", redirect: "/home" },
  {
    path: "/login/callback",
    component: LoginCallback,
    name: "LoginCallback",
  },
  {
    path: "/maintenance",
    name: "Maintenance",
    component: () => import("../views/Maintenance.vue"),
  },
  {
    path: "/",
    name: "Dashboard",
    component: () => import("../views/Dashboard.vue"),
    beforeEnter: [navigationGuard],
    children: [
      { path: "", name: "basic", redirect: "home" },
      {
        path: "report",
        name: "Report",
        meta: {
          title: translate("nav.report"),
        },
        component: () => import("../views/Report.vue"),
      },
      {
        path: "economy-energy",
        name: "economyEnergy",
        component: () => import("../views/client-ext/EconomyEnergy.vue"),
        meta: {
          title: translate("nav.economyenergy"),
        },
      },
      {
        path: "user-preferences",
        name: "UserPreferences",
        component: () => import("../views/user/UserPreferences.vue"),
      },
      {
        path: "home",
        name: "home",
        component: () => import("../views/Home.vue"),
      },
      {
        path: "manual-config",
        name: "ManualVariableConfig",
        meta: {
          title: translate("nav.manualConfig"),
        },
        component: () => import("../views/variables/ManualConfig.vue"),
      },
      {
        path: "automatic-variables",
        name: "AutomaticVariables",
        meta: {
          title: translate("nav.manageVariables"),
        },
        component: () =>
          import("../views/variables/AutomaticVariableContainer.vue"),
        children: [
          {
            path: "",
            name: "AutomaticVariablesList",
            component: () =>
              import(
                "../views/variables/edit-variables/AutomaticVariablesList.vue"
              ),
          },
          {
            path: "historychangevar/:projectId",
            name: "HistoryChangeVar",
            component: () =>
              import("../views/variables/edit-variables/HistoryChangeVar.vue"),
          },
          {
            path: "AddVarsToProject/:projectId",
            name: "AddVarsToProject",
            component: () =>
              import("../views/variables/edit-variables/AddVarsToProject.vue"),
          },
          {
            path: "variable/:projectId/:placeId/:variableId",
            name: "editVariable",
            component: () =>
              import("../views/variables/edit-variables/VariableDetail.vue"),
          },
          {
            path: "formula/:projectId/:formulaId",
            name: "editFormula",
            component: () =>
              import("../views/variables/edit-variables/Formula.vue"),
          },
          {
            path: "formula/:projectId",
            name: "createFormula",
            component: () =>
              import("../views/variables/edit-variables/Formula.vue"),
          },
          {
            path: "manual/:projectId/:variableId",
            name: "editManualVariable",
            component: () =>
              import("../views/variables/edit-variables/ManualVariable.vue"),
          },
          {
            path: "manual/:projectId",
            name: "createManualVariable",
            component: () =>
              import("../views/variables/edit-variables/ManualVariable.vue"),
          },
        ],
      },
      {
        path: "data-self-service",
        name: "dataSelfService",
        meta: {
          title: translate("nav.selfData"),
        },
        component: () => import("../views/self-data/Search.vue"),
      },
      {
        path: "alerting-config",
        name: "alerting config",
        meta: {
          title: translate("nav.alertingConfig"),
        },
        component: () => import("../views/alerting/AlertingContainer.vue"),
      },
      {
        path: "interventions",
        name: "BexTicket",
        meta: {
          title: translate("bexTicket.listTicket"),
        },
        component: () => import("../views/bex-ticket/BexTicketContainer.vue"),
        children: [
          {
            name: "bexticket-main",
            path: "",
            component: () => import("../views/bex-ticket/BexTicketList.vue"),
          },
          {
            path: "edit/:interventionId",
            name: "bexticket-edit",
            component: () => import("../views/bex-ticket/BexTicketAdd.vue"),
          },
          {
            path: "add",
            name: "bexticket-add",
            component: () => import("../views/bex-ticket/BexTicketAdd.vue"),
          },
        ],
      },
      {
        path: "bexrapports",
        name: "BexRapport",
        meta: {
          title: translate("nav.report"),
        },
        component: () => import("../views/bex-rapport/BexRapportContainer.vue"),
        children: [
          {
            name: "bexrapport-main",
            path: "",
            component: () => import("../views/bex-rapport/BexRapportList.vue"),
          },
          {
            path: "edit/:rapportId",
            name: "bexrapport-edit",
            component: () => import("../views/bex-rapport/BexRapportAdd.vue"),
          },
          {
            path: "add",
            name: "bexrapport-add",
            component: () => import("../views/bex-rapport/BexRapportAdd.vue"),
          },
        ],
      },
      {
        path: "cperapports",
        name: "CpeRapport",
        meta: {
          title: translate("nav.report"),
        },
        component: () => import("../views/cpe-rapport/CpeRapportContainer.vue"),
        children: [
          {
            name: "cperapport-main",
            path: "",
            component: () => import("../views/cpe-rapport/CpeRapportList.vue"),
          },
          {
            path: "edit/:rapportId",
            name: "cperapport-edit",
            component: () => import("../views/cpe-rapport/CpeRapportAdd.vue"),
          },
          {
            path: "add",
            name: "cperapport-add",
            component: () => import("../views/cpe-rapport/CpeRapportAdd.vue"),
          },
          {
            path: "addprogram",
            name: "cpeautorapport-add",
            component: () =>
              import("../views/cpe-rapport/CpeAutoRapportAdd.vue"),
          },
          {
            path: "editprogram/:rapportId",
            name: "cpeautorapport-edit",
            component: () =>
              import("../views/cpe-rapport/CpeAutoRapportAdd.vue"),
          },
          {
            path: "reporhistory",
            name: "cpeautorapport-history",
            component: () =>
              import("../views/cpe-rapport/CpeAutoRapportHistory.vue"),
          },
        ],
      },
      {
        path: "events",
        name: "Event",
        meta: {
          title: translate("nav.events"),
        },
        component: () => import("../views/events/EventContainer.vue"),
        children: [
          {
            name: "event-main",
            path: "",
            component: () => import("../views/events/EventList.vue"),
          },
          {
            path: "details/:eventId",
            name: "event-details",
            component: () => import("../views/events/EventDetail.vue"),
          },
        ],
      },
      {
        path: "data-explorer",
        name: "data explorer",
        component: () => import("../views/data-explorer/DataExplorer.vue"),
        children: [
          {
            name: "Data-explorer-main",
            path: "",
            meta: {
              title: translate("nav.dataExplorer"),
            },
            component: () => import("../views/data-explorer/DashboardList.vue"),
          },
          {
            path: "dash/:dashId",
            meta: {
              title: translate("dataExplorer.dashDetail"),
            },
            component: () =>
              import("../views/data-explorer/DashboardDetail.vue"),
          },
          {
            path: "graph/:dashId/:graphId",
            meta: {
              title: translate("dataExplorer.dashDetail"),
            },
            component: () => import("../views/data-explorer/GraphDetails.vue"),
          },
        ],
      },
      {
        path: "data-analytics",
        name: "data analytics",
        meta: {
          title: translate("nav.dataAnalytics"),
        },
        component: () => import("../views/data-analytics/DataAnalytics.vue"),
      },
      {
        path: "parameters",
        component: () => import("../views/parameters/ParametersContainer.vue"),
        children: [
          {
            path: "clients",
            name: "clientList",
            meta: {
              title: translate("nav.clients"),
            },
            component: () =>
              import("../views/parameters/clients/ClientList.vue"),
          },
          {
            path: "places",
            name: "placeList",
            meta: {
              title: translate("nav.places"),
            },
            component: () => import("../views/parameters/places/PlaceList.vue"),
          },
          {
            path: "projects",
            name: "projectList",
            meta: {
              title: translate("nav.projects"),
            },
            component: () =>
              import("../views/parameters/projects/ProjectList.vue"),
          },
          {
            path: "users",
            name: "usersList",
            meta: {
              title: translate("nav.users"),
            },
            component: () => import("../views/parameters/users/UsersList.vue"),
          },
          {
            path: "contacts",
            name: "contactsList",
            meta: {
              title: translate("nav.contacts"),
            },
            component: () =>
              import("../views/parameters/contacts/ContactsList.vue"),
          },
          {
            path: "permissions",
            name: "Permissions",
            meta: {
              title: translate("nav.permissions"),
            },
            component: () =>
              import("../views/parameters/permissions/Permissions.vue"),
          },
        ],
      },
      {
        path: "/parameters/clients/:clientId",
        meta: {
          title: translate("titles.ClientDetail"),
        },
        component: () =>
          import(
            "../views/parameters/clients/clientDetail/ClientDetailContainer.vue"
          ),
        children: [
          {
            path: "main-informations",
            name: "clientDetail",
            component: () =>
              import(
                "../views/parameters/clients/clientDetail/ClientDetailMainInformations.vue"
              ),
          },
          {
            path: "places",
            name: "clientDetailPlaces",
            component: () =>
              import(
                "../views/parameters/clients/clientDetail/ClientDetailPlaces.vue"
              ),
          },
        ],
      },
      {
        path: "/parameters/users/:userId",
        meta: {
          title: translate("titles.UserDetail"),
        },
        component: () =>
          import(
            "../views/parameters/users/userDetail/UserDetailContainer.vue"
          ),
        children: [
          {
            path: "main-informations",
            name: "userDetail",
            component: () =>
              import(
                "../views/parameters/users/userDetail/UserDetailMainInformations.vue"
              ),
          },
        ],
      },
      {
        path: "parameters/places/:placeId",
        meta: {
          title: translate("titles.SiteDetail"),
        },
        component: () =>
          import(
            "../views/parameters/places/placeDetail/PlaceDetailContainer.vue"
          ),
        children: [
          {
            path: "main-informations",
            name: "placeDetail",
            component: () =>
              import(
                "../views/parameters/places/placeDetail/PlaceDetailMainInformations.vue"
              ),
          },
          {
            path: "scenarios",
            name: "placeDetailScenarios",
            component: () =>
              import(
                "../views/parameters/places/placeDetail/PlaceDetailScenarios.vue"
              ),
          },
          {
            path: "projets",
            name: "placeDetailProjects",
            component: () =>
              import(
                "../views/parameters/places/placeDetail/PlaceDetailProjects.vue"
              ),
          },
        ],
      },
      {
        path: "parameters/projects/:projectId",
        meta: {
          title: translate("titles.ProjectDetail"),
        },
        component: () =>
          import(
            "../views/parameters/projects/projectDetail/ProjectDetailContainer.vue"
          ),
        children: [
          {
            path: "main-informations",
            name: "projectDetail",
            component: () =>
              import(
                "../views/parameters/projects/projectDetail/ProjectDetailMainInformations.vue"
              ),
          },
          {
            path: "main-informations",
            name: "projectBilan",
            component: () =>
              import(
                "../views/parameters/projects/projectDetail/ProjectBilan.vue"
              ),
          },
          {
            path: "main-informations",
            name: "projectEnergie",
            component: () =>
              import(
                "../views/parameters/projects/projectDetail/projectEnergie.vue"
              ),
          },
          {
            path: "main-informations",
            name: "projectTechnique",
            component: () =>
              import(
                "../views/parameters/projects/projectDetail/projectTechnique.vue"
              ),
          },
          {
            path: "main-informations",
            name: "FilesTechnique",
            component: () =>
              import(
                "../views/parameters/projects/projectDetail/FilesTechnique.vue"
              ),
          },
        ],
      },
      {
        path: "synoptique",
        name: "SynopticView",
        meta: {
          title: translate("nav.synoptique"),
        },
        component: () => import("../views/synoptique/SynopticView.vue"),
      },
    ],
  },
  {
    path: "/login",
    name: "Login",
    component: () => import("../views/Login.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: "active",
});

const DEFAULT_TITLE = "Emeely";
router.beforeEach((to, _from, next) => {
  const title =
    to.meta && to.meta.title
      ? `${DEFAULT_TITLE} - ${to.meta.title as string} `
      : DEFAULT_TITLE;
  document.title = title;
  next();
});

router.onError((error) => {
  console.error(error);
  if (
    /ChunkLoadError: Loading chunk [0-9a-zA-Z]+ failed./i.test(error.message)
  ) {
    window.location.reload();
  }
});
export default router;
